import { useEffect, useState } from "react";
import { MapContainer, Polyline } from "react-leaflet";
import MapTiles from "../components/MapTiles";
import { toast } from "sonner";
import { Trash2, Flame, X } from "lucide-react";
import api from "../lib/api";
import { fmtDistance, fmtDuration, fmtPace, fmtDateTime } from "../lib/format";

const EMPTY_IMG = "https://images.pexels.com/photos/10380537/pexels-photo-10380537.jpeg";

function bounds(route) {
  const lats = route.map((p) => p.lat);
  const lngs = route.map((p) => p.lng);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

// Per-kilometer splits. Bar length is relative to the slowest split, so the
// fast and slow kilometers of this particular run stand out against each
// other; the quickest one is highlighted.
function Splits({ splits }) {
  if (!splits || splits.length === 0) return null;

  const paceOf = (s) => (s.distance_m > 0 ? s.duration_s / 60 / (s.distance_m / 1000) : 0);
  const paces = splits.map(paceOf).filter((p) => p > 0);
  if (paces.length === 0) return null;
  const slowest = Math.max(...paces);
  const fastest = Math.min(...paces);

  return (
    <div className="px-5 pb-6" data-testid="run-splits">
      <div className="data-label mb-3">Splits</div>
      <div className="space-y-2">
        {splits.map((s) => {
          const pace = paceOf(s);
          const partial = s.distance_m < 990;
          const isFastest = !partial && pace === fastest && splits.length > 1;
          return (
            <div key={s.km} className="flex items-center gap-3">
              <div className="w-10 shrink-0 text-sm tabular-nums text-neutral-400">
                {partial ? fmtDistance(s.distance_m) : `${s.km}`}
                <span className="text-[10px] text-neutral-600 ml-0.5">km</span>
              </div>
              <div className="flex-1 h-7 bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
                <div
                  className={`h-full rounded-lg ${isFastest ? "bg-[#CCFF00]" : "bg-white/15"}`}
                  style={{ width: `${slowest > 0 ? Math.max(8, (pace / slowest) * 100) : 0}%` }}
                />
              </div>
              <div
                className={`w-16 shrink-0 text-right text-sm tabular-nums ${
                  isFastest ? "text-[#CCFF00] font-bold" : "text-neutral-200"
                }`}
              >
                {fmtPace(pace)}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-neutral-600 mt-3">
        Balkenlänge = Pace im Verhältnis zum langsamsten Kilometer. Kürzer ist schneller.
      </p>
    </div>
  );
}

function RunDetail({ run, onClose }) {
  const hasRoute = run.route && run.route.length > 1;
  return (
    <div className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="run-detail">
      <div className="bg-[#0A0A0A] border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between p-5 sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <div className="data-label">Lauf-Details</div>
            <div className="text-sm text-neutral-400">{fmtDateTime(run.started_at)}</div>
          </div>
          <button onClick={onClose} data-testid="close-detail" className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center active:scale-95 transition-transform">
            <X className="w-5 h-5" />
          </button>
        </div>

        {hasRoute && (
          <div className="h-64 dark-map mx-5 rounded-2xl overflow-hidden border border-white/10">
            <MapContainer bounds={bounds(run.route)} zoomControl={false} attributionControl={false} style={{ height: "100%", width: "100%" }}>
              <MapTiles />
              <Polyline positions={run.route.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#CCFF00", weight: 5 }} />
            </MapContainer>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 p-5">
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-4">
            <div className="data-label mb-1">Distanz</div>
            <div className="font-display font-black text-3xl tracking-tighter text-[#CCFF00]">{fmtDistance(run.distance_m)}<span className="text-sm text-neutral-500 ml-1">km</span></div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-4">
            <div className="data-label mb-1">Zeit</div>
            <div className="font-display font-black text-3xl tracking-tighter">{fmtDuration(run.duration_s)}</div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-4">
            <div className="data-label mb-1">Ø Pace</div>
            <div className="font-display font-black text-3xl tracking-tighter">{fmtPace(run.pace_min_km)}<span className="text-sm text-neutral-500 ml-1">/km</span></div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-4">
            <div className="data-label mb-1">Kalorien</div>
            <div className="font-display font-black text-3xl tracking-tighter flex items-center gap-1">
              {run.calories != null ? Math.round(run.calories) : "--"}
              <Flame className="w-5 h-5 text-[#FF3366]" />
            </div>
          </div>
        </div>
        {run.calories == null && (
          <p className="text-xs text-neutral-500 px-5 pb-5">Trage Alter & Gewicht im Profil ein, um Kalorien zu berechnen.</p>
        )}

        <Splits splits={run.splits} />
      </div>
    </div>
  );
}

export default function History() {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/runs").then(({ data }) => setRuns(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Diesen Lauf wirklich löschen?")) return;
    try {
      await api.delete(`/runs/${id}`);
      setRuns((prev) => prev.filter((r) => r.id !== id));
      toast.success("Lauf gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Verlauf</h1>

      {loading ? (
        <div className="text-neutral-500 text-sm">Laden...</div>
      ) : runs.length === 0 ? (
        <div className="bg-[#121212] border border-white/10 rounded-3xl overflow-hidden" data-testid="history-empty">
          <img src={EMPTY_IMG} alt="Laufschuhe" className="w-full h-48 object-cover opacity-70" />
          <div className="p-6 text-center">
            <p className="font-display font-black text-xl tracking-tighter mb-1">Noch keine Läufe</p>
            <p className="text-neutral-400 text-sm">Starte deinen ersten Lauf über den "Los"-Button.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="runs-list">
          {runs.map((r) => {
            const hasRoute = r.route && r.route.length > 1;
            return (
              <div
                key={r.id}
                data-testid={`run-item-${r.id}`}
                onClick={() => setSelected(r)}
                className="bg-[#121212] border border-white/10 rounded-2xl p-4 flex items-center gap-4 active:scale-[0.99] transition-transform cursor-pointer"
              >
                <div className="w-16 h-16 rounded-xl bg-[#1A1A1A] border border-white/10 overflow-hidden shrink-0 dark-map">
                  {hasRoute ? (
                    <MapContainer bounds={bounds(r.route)} zoomControl={false} attributionControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} style={{ height: "100%", width: "100%", pointerEvents: "none" }}>
                      <MapTiles />
                      <Polyline positions={r.route.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#CCFF00", weight: 3 }} />
                    </MapContainer>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-600 text-xs">GPS</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-2xl tracking-tighter">
                    {fmtDistance(r.distance_m)} <span className="text-sm text-neutral-500">km</span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">{fmtDateTime(r.started_at)}</div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {fmtDuration(r.duration_s)} · {fmtPace(r.pace_min_km)} /km
                  </div>
                </div>
                <button onClick={(e) => del(r.id, e)} data-testid={`delete-run-${r.id}`} className="w-10 h-10 rounded-full flex items-center justify-center text-neutral-600 hover:text-[#FF3366] transition-colors">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {selected && <RunDetail run={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
